import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { TrustBadge } from "@/components/TrustBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Wallet, UserPlus, Trash2, Check, Clock, ShieldCheck } from "lucide-react";

export default function Account() {
  const { user, loading, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [payout, setPayout] = useState("");
  const [saving, setSaving] = useState(false);
  const [refName, setRefName] = useState("");
  const [refEmail, setRefEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await api.get("/profile");
    setProfile(data);
    setPayout(data.payout_details || "");
  };

  useEffect(() => {
    if (user) load().catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  if (loading) return <div className="min-h-screen bg-[#0B0E14] text-slate-400 flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const savePayout = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { payout_details: payout });
      toast.success("Payout details saved");
      load();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const addRef = async () => {
    if (!refName || !refEmail) {
      toast.error("Name and email required");
      return;
    }
    setAdding(true);
    try {
      await api.post("/references", { name: refName, email: refEmail });
      toast.success(`Vouch request emailed to ${refName}`);
      setRefName(""); setRefEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add reference");
    } finally {
      setAdding(false);
    }
  };

  const delRef = async (id) => {
    await api.delete(`/references/${id}`);
    load();
  };

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white">Verification & Trust</h1>
            <p className="text-sm text-slate-400">Build trust so investors back you with confidence.</p>
          </div>
        </div>

        {profile?.trust && <TrustBadge trust={profile.trust} />}

        {/* Payout details */}
        <section className="rounded-xl bg-[#121721] border border-white/10 p-5 space-y-3">
          <div className="flex items-center gap-2 text-white font-semibold">
            <Wallet className="h-4 w-4 text-amber-400" /> Payout destination
          </div>
          <p className="text-xs text-slate-500">Where released funds are sent. Shown to partners so they know who they're paying.</p>
          <Textarea
            data-testid="payout-input"
            value={payout}
            onChange={(e) => setPayout(e.target.value)}
            placeholder="e.g. Bank name + account holder, PayPal email, or wallet address"
            rows={3}
            className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500 resize-none"
          />
          <Button data-testid="save-payout-btn" onClick={savePayout} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-10">
            {saving ? "Saving…" : "Save payout details"}
          </Button>
        </section>

        {/* References */}
        <section className="rounded-xl bg-[#121721] border border-white/10 p-5 space-y-4">
          <div className="flex items-center gap-2 text-white font-semibold">
            <UserPlus className="h-4 w-4 text-amber-400" /> References who vouch for you
          </div>
          <p className="text-xs text-slate-500">Add 1–2 people who'll co-sign your reputation. They get an email link to vouch.</p>

          <div className="space-y-3">
            {(profile?.references || []).map((r) => (
              <div key={r.ref_id} data-testid={`ref-${r.ref_id}`} className="flex items-center justify-between rounded-lg bg-[#0B0E14] border border-white/10 p-3">
                <div>
                  <div className="text-sm text-white font-medium">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.email}{r.relationship ? ` · ${r.relationship}` : ""}</div>
                  {r.note && <div className="text-xs text-slate-400 mt-1">"{r.note}"</div>}
                </div>
                <div className="flex items-center gap-2">
                  {r.status === "confirmed" ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-400 font-semibold"><Check className="h-3.5 w-3.5" /> Vouched</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Clock className="h-3.5 w-3.5" /> Pending</span>
                  )}
                  <button data-testid={`del-ref-${r.ref_id}`} onClick={() => delRef(r.ref_id)} className="text-slate-600 hover:text-amber-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Reference name</Label>
              <Input data-testid="ref-name-input" value={refName} onChange={(e) => setRefName(e.target.value)} className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Reference email</Label>
              <Input data-testid="ref-email-input" type="email" value={refEmail} onChange={(e) => setRefEmail(e.target.value)} className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
            </div>
          </div>
          <Button data-testid="add-ref-btn" onClick={addRef} disabled={adding} variant="outline" className="rounded-full h-10 border-white/15 bg-white/5 text-white hover:bg-white/10">
            <UserPlus className="h-4 w-4 mr-1.5" /> {adding ? "Sending…" : "Send vouch request"}
          </Button>
        </section>
      </div>
    </div>
  );
}
